/** Stable IDs are URL contracts. Labels and ordering may change independently. */
export type MonitorTabId =
  'analytics-info' | 'device-cli' | 'alerts' | 'attack-shield' | 'sipwatch' |
  'SDWAN' | 'device-config' | 'qos' | 'overlay' | 'overlay-plus' | '4glte' |
  'device-system' | 'ethernet' | 'lan-jump' | 'auth-logs' | 'interfaces' |
  'dhcp' | 'system' | 'memory' | 'network' | 'wifi' | 'wifi-clients' |
  'wifi-network' | 'general-settings' | 'logging' | 'asset-groups' | 'ras' | 'notes';

export interface MonitorTab {
  readonly id: MonitorTabId;
  readonly label: string;
  readonly children?: ReadonlyArray<MonitorTab>;
}

export interface MonitorCapabilities {
  readonly diagnostics: boolean;
  readonly team: boolean;
  readonly qos: boolean;
  readonly wireless: boolean;
  readonly lte: boolean;
  readonly statistics: boolean;
  readonly jump: boolean;
  readonly ras: boolean;
}

const tab = (id: MonitorTabId, label: string, children?: ReadonlyArray<MonitorTab>): MonitorTab =>
  ({ id, label, children });

export function buildMonitorTabs(c: MonitorCapabilities): ReadonlyArray<MonitorTab> {
  const sdwan: MonitorTab[] = [];
  if (c.team) sdwan.push(tab('device-config', 'Team Links'));
  if (c.qos) sdwan.push(tab('qos', 'QoS'));
  // Both overlays have always been available, independently of team configuration.
  sdwan.push(tab('overlay', 'Overlay'), tab('overlay-plus', 'Overlay Plus'));
  const wifi = [tab('wifi-clients', 'Clients')];
  if (c.wireless) wifi.push(tab('wifi-network', 'WiFi Config'));
  const system = [tab('ethernet', 'Ethernet Ports & VLANs')];
  if (c.jump) system.push(tab('lan-jump', 'LAN Jump'));
  system.push(tab('auth-logs', 'Auth Logs'), tab('interfaces', 'Interfaces'),
    tab('dhcp', 'Active DHCP'), tab('system', 'System'), tab('memory', 'Memory'),
    tab('network', 'Network'), tab('wifi', 'WiFi', wifi),
    tab('general-settings', 'General Settings'), tab('logging', 'Logging'), tab('asset-groups', 'Asset Groups'));
  const tabs = [tab('analytics-info', 'Analytics & Info')];
  if (c.diagnostics) tabs.push(tab('device-cli', 'Diagnostics'));
  tabs.push(tab('alerts', 'Alerts'), tab('attack-shield', 'Attack Shield'),
    tab('sipwatch', 'SIPWatch'), tab('SDWAN', 'SDWAN', sdwan));
  if (c.lte) tabs.push(tab('4glte', '4GLTE'));
  if (c.statistics || c.jump || c.wireless) tabs.push(tab('device-system', 'Device System', system));
  if (c.ras) tabs.push(tab('ras', 'RAS'));
  tabs.push(tab('notes', 'Notes'));
  return tabs;
}

function findPath(tabs: ReadonlyArray<MonitorTab>, id: string): MonitorTab[] {
  for (const item of tabs) {
    if (item.id === id) return [item];
    const childPath = item.children ? findPath(item.children, id) : [];
    if (childPath.length) return [item, ...childPath];
  }
  return [];
}

const allTabs = buildMonitorTabs({ diagnostics: true, team: true, qos: true, wireless: true,
  lte: true, statistics: true, jump: true, ras: true });

/** Semantic IDs win over legacy numeric suffixes, including when a tab is unavailable. */
export function resolveMonitorPath(tabs: ReadonlyArray<MonitorTab>, type: string): MonitorTabId[] {
  if (!tabs.length) return [];
  const rawId = (type || '').replace(/-\d+$/, '');
  const id = rawId === 'team-links' ? 'SDWAN' : rawId;
  let path = findPath(tabs, id);
  if (!path.length) {
    // A removed child falls back to its available parent, never an unrelated tab.
    const knownPath = findPath(allTabs, id);
    path = knownPath.length ? findPath(tabs, knownPath[0].id) : [];
    if (!path.length) {
      const index = Number((type || '').match(/-(\d+)$/)?.[1]) - 1;
      path = [!knownPath.length && Number.isInteger(index) && tabs[index] ? tabs[index] : tabs[0]];
    }
  }
  let last = path[path.length - 1];
  while (last.children && last.children.length) {
    last = last.children[0];
    path.push(last);
  }
  return path.map(item => item.id);
}

export function childTabs(tabs: ReadonlyArray<MonitorTab>, id: MonitorTabId): ReadonlyArray<MonitorTab> {
  const item = tabs.find(candidate => candidate.id === id);
  return item && item.children || [];
}
